import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MrfLocationFilters } from './hr-http.service';

export type MrfDashboardFilterTab = 'ALL' | 'REQUESTED' | 'PENDING' | 'APPROVED' | 'REJECTED';

export type MrfFilterDialogMode = 'default' | 'customize';
export type MrfFilterDialogField = 'los' | 'state' | 'city' | 'zone' | 'subZone' | 'deanName' | 'campus';

export interface MrfDashboardSummaryPeriodState {
  selectedSummaryPeriod: 'all' | number;
  summaryMonthYear: number;
}

export interface MrfFilterDialogCustomizeRowState {
  id: number;
  filterBy: MrfFilterDialogField | null;
  selectedValue: any | null;
  options: any[];
  searchText: string;
}

export interface MrfFilterDialogState {
  filterMode: MrfFilterDialogMode;
  selectedLos: any | null;
  selectedState: any | null;
  selectedCity: any | null;
  selectedZone: any | null;
  selectedSubZone: any | null;
  selectedDean: any | null;
  selectedCampus: any | null;
  losList: any[];
  states: any[];
  cities: any[];
  zones: any[];
  subZones: any[];
  deans: any[];
  campuses: any[];
  searchTexts: {
    los: string;
    state: string;
    city: string;
    zone: string;
    subZone: string;
    dean: string;
    campus: string;
  };
  appliedLos: string;
  appliedStateId: number | null;
  appliedCityId: number | null;
  appliedZoneName: string;
  appliedSubZone: string;
  appliedDean: string;
  customizeRows: MrfFilterDialogCustomizeRowState[];
  customizeRowId: number;
}

/**
 * Keeps MRF dashboard UI state (search / tab / location filters) across navigations
 * to View forms and back, using observables.
 */
@Injectable({
  providedIn: 'root',
})
export class MrfDashboardStateService {
  private readonly quickFilterSubject = new BehaviorSubject<string>('');
  private readonly activeTabSubject = new BehaviorSubject<MrfDashboardFilterTab>('ALL');
  private readonly locationFiltersSubject = new BehaviorSubject<MrfLocationFilters>({});
  private readonly filterModeSubject = new BehaviorSubject<MrfFilterDialogMode>('default');
  private readonly customizePrimaryFilterBySubject =
    new BehaviorSubject<MrfFilterDialogField | null>(null);
  private readonly filterDialogStateSubject = new BehaviorSubject<MrfFilterDialogState | null>(null);
  private readonly summaryPeriodSubject = new BehaviorSubject<MrfDashboardSummaryPeriodState>(
    this.createDefaultSummaryPeriod()
  );

  readonly quickFilter$: Observable<string> = this.quickFilterSubject.asObservable();
  readonly activeTab$: Observable<MrfDashboardFilterTab> = this.activeTabSubject.asObservable();
  readonly locationFilters$: Observable<MrfLocationFilters> =
    this.locationFiltersSubject.asObservable();
  readonly filterMode$: Observable<MrfFilterDialogMode> = this.filterModeSubject.asObservable();
  readonly customizePrimaryFilterBy$: Observable<MrfFilterDialogField | null> =
    this.customizePrimaryFilterBySubject.asObservable();
  readonly filterDialogState$: Observable<MrfFilterDialogState | null> =
    this.filterDialogStateSubject.asObservable();
  readonly summaryPeriod$: Observable<MrfDashboardSummaryPeriodState> =
    this.summaryPeriodSubject.asObservable();

  get quickFilter(): string {
    return this.quickFilterSubject.value;
  }

  get activeTab(): MrfDashboardFilterTab {
    return this.activeTabSubject.value;
  }

  get locationFilters(): MrfLocationFilters {
    return this.locationFiltersSubject.value;
  }

  get filterMode(): MrfFilterDialogMode {
    return this.filterModeSubject.value;
  }

  get customizePrimaryFilterBy(): MrfFilterDialogField | null {
    return this.customizePrimaryFilterBySubject.value;
  }

  get filterDialogState(): MrfFilterDialogState | null {
    return this.filterDialogStateSubject.value;
  }

  get summaryPeriod(): MrfDashboardSummaryPeriodState {
    return this.summaryPeriodSubject.value;
  }

  get selectedSummaryPeriod(): 'all' | number {
    return this.summaryPeriodSubject.value.selectedSummaryPeriod;
  }

  get summaryMonthYear(): number {
    return this.summaryPeriodSubject.value.summaryMonthYear;
  }

  private createDefaultSummaryPeriod(): MrfDashboardSummaryPeriodState {
    return {
      selectedSummaryPeriod: new Date().getMonth() + 1,
      summaryMonthYear: new Date().getFullYear(),
    };
  }

  setQuickFilter(value: string): void {
    this.quickFilterSubject.next(String(value ?? ''));
  }

  setActiveTab(tab: MrfDashboardFilterTab): void {
    this.activeTabSubject.next(tab);
  }

  setLocationFilters(filters: MrfLocationFilters): void {
    this.locationFiltersSubject.next(filters ?? {});
  }

  setFilterMode(mode: MrfFilterDialogMode): void {
    this.filterModeSubject.next(mode);
  }

  setCustomizePrimaryFilterBy(filterBy: MrfFilterDialogField | null): void {
    this.customizePrimaryFilterBySubject.next(filterBy);
  }

  setFilterDialogState(state: MrfFilterDialogState | null): void {
    this.filterDialogStateSubject.next(state);
  }

  setSummaryPeriod(selectedSummaryPeriod: 'all' | number, summaryMonthYear?: number): void {
    const current = this.summaryPeriodSubject.value;
    this.summaryPeriodSubject.next({
      selectedSummaryPeriod,
      summaryMonthYear: summaryMonthYear ?? current.summaryMonthYear,
    });
  }
}
